import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { VERIFICATION_EMAIL_LS_KEY } from "@/data/constants";
import { POSTHOG_EVENT } from "@/services/posthog/data";
import useTracker from "@/tracking/useTracker";
import { useMutation } from "@tanstack/react-query";
import { sendEmailVerification } from "firebase/auth";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useUser } from "reactfire";
import { MailForward } from "@pkg/icons";
import {
  getRemainingCooldownMs,
  isEmailVerificationCooldownActive,
  startEmailVerificationCooldown,
} from "./utils";

const VerifyEmailBanner = () => {
  const { data: userData } = useUser();
  const { track } = useTracker();

  const [isVerificationEmailSent, setisVerificationEmailSent] = useState(false);
  const [isEmailVerificationOnCooldown, setIsEmailVerificationOnCooldown] =
    useState(isEmailVerificationCooldownActive());

  const handleEmailVerification = async () => {
    userData && (await sendEmailVerification(userData));
  };

  const { mutate: mutateEmailverification, isLoading } = useMutation(
    handleEmailVerification,
    {
      onSuccess: () => {
        toast.success("Email Verification Sent!");

        const oldValue = JSON.parse(
          localStorage.getItem(VERIFICATION_EMAIL_LS_KEY) || "[]",
        );

        const newVal = [...oldValue, userData?.email];

        localStorage.setItem(VERIFICATION_EMAIL_LS_KEY, JSON.stringify(newVal));

        startEmailVerificationCooldown();
        setIsEmailVerificationOnCooldown(true);

        if (!isVerificationEmailSent) setisVerificationEmailSent(true);
      },
      onError: () => {
        toast.error("Could not send Verification Link!");
      },
    },
  );

  const handleVerify = () => {
    mutateEmailverification();
    track(POSTHOG_EVENT.VERIFY_EMAIL_BUTTON_CLICKED);
  };

  useEffect(() => {
    if (!isEmailVerificationOnCooldown) return;
    const remaining = getRemainingCooldownMs();
    if (remaining <= 0) {
      setIsEmailVerificationOnCooldown(false);
      return;
    }
    const timer = setTimeout(
      () => setIsEmailVerificationOnCooldown(false),
      remaining,
    );
    return () => clearTimeout(timer);
  }, [isEmailVerificationOnCooldown]);

  useEffect(() => {
    const emailVerificationSentEmailProof =
      localStorage.getItem(VERIFICATION_EMAIL_LS_KEY) ?? "[]"; // Default to an empty array as a string

    const emailProofArray = JSON.parse(emailVerificationSentEmailProof);

    if (
      Array.isArray(emailProofArray) &&
      emailProofArray.includes(userData?.email ?? "")
    ) {
      setisVerificationEmailSent(true);
    }
  }, [userData]);

  if (isEmailVerificationOnCooldown) return null;

  return (
    <Card>
      <div className="flex w-full flex-col justify-between gap-4 text-start sm:flex-row sm:items-center">
        <div className="flex gap-4 sm:items-center">
          <div className="rounded-xl bg-surface-accent p-3">
            <MailForward />
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-sm sm:text-lg md:text-2xl">
              Thanks for Signing Up to Aftershoot!
            </p>
            <p className="text-xs font-light sm:text-base md:text-base">
              {isVerificationEmailSent
                ? `Verification email sent to ${userData?.email ?? ""}.`
                : "Please verify your email."}
            </p>
          </div>
        </div>

        <Button
          disabled={isLoading}
          onClick={handleVerify}
          variant="outline"
          size="sm"
          className="w-full sm:w-auto"
          // className="rounded-md border border-nightRider px-6 py-2 text-xs transition-all hover:border-white hover:bg-black hover:text-white sm:px-8 sm:py-4 sm:text-base"
        >
          {isVerificationEmailSent ? "Resend Email" : " Verify Email"}
        </Button>
      </div>
    </Card>
  );
};

export default VerifyEmailBanner;
